import { useCallback, useEffect, useRef, useState } from "react";
import {
  createService,
  deleteService,
  fetchServices,
  updateService,
  updateServiceStatus,
} from "../services/servicesService";
import { fetchServiceTypes } from "../services/serviceTypesService";

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const useServices = () => {
  const [services, setServices] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const totalItemsRef = useRef(0);

  const loadServices = useCallback(
    async ({ page = 1, search = "" } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchServices({
          page,
          total: totalItemsRef.current,
          search,
        });

        if (response.success) {
          const { items = [], pagination } = response.data || {};
          setServices(items);

          if (pagination) {
            const { total_pages, current_page, totalItems: totalItemsValue } = pagination;
            setTotalPages(total_pages || 1);
            setCurrentPage(current_page || page || 1);
            if (typeof totalItemsValue === "number") {
              totalItemsRef.current = totalItemsValue;
              setTotalItems(totalItemsValue);
            }
          }
          setSuccess(null);
        } else {
          setError(response.message || DEFAULT_ERROR_MESSAGE);
        }
      } catch (err) {
        console.error("Error fetching services:", err);
        setError("Failed to fetch services. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadServiceTypes = useCallback(async () => {
    try {
      const response = await fetchServiceTypes({ limit: 0 });
      if (response.success) {
        setServiceTypes(response.data?.items || []);
      }
    } catch (err) {
      console.error("Error fetching service types:", err);
    }
  }, []);

  const handleCreate = useCallback(
    async (formData) => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const response = await createService(formData);
        if (response.success) {
          setSuccess("Service added successfully.");
          await loadServices({ page: currentPage, search: searchValue });
          return { success: true };
        }

        setError(response.message || "Failed to add service.");
        return { success: false };
      } catch (err) {
        console.error("Error creating service:", err);
        setError("An error occurred while creating the service.");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [currentPage, loadServices, searchValue]
  );

  const handleUpdate = useCallback(
    async (id, formData) => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const response = await updateService({ id, data: formData });
        if (response.success) {
          setSuccess("Service updated successfully.");
          await loadServices({ page: currentPage, search: searchValue });
          return { success: true };
        }

        setError(response.message || "Failed to update service.");
        return { success: false };
      } catch (err) {
        console.error("Error updating service:", err);
        setError("An error occurred while updating the service.");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [currentPage, loadServices, searchValue]
  );

  const handleDelete = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await deleteService(id);
      if (response.success) {
        setServices((prev) => prev.filter((item) => item.id !== id));
        setSuccess("Service deleted successfully.");
        return { success: true };
      }

      setError(response.message || "Failed to delete service.");
      return { success: false };
    } catch (err) {
      console.error("Error deleting service:", err);
      setError("An error occurred while deleting the service.");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStatusToggle = useCallback(async (id, currentStatus) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const newStatus = currentStatus === "active" ? "inactive" : "active";

    try {
      const response = await updateServiceStatus({ id, status: newStatus });
      if (response.success) {
        setServices((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, status: newStatus } : item
          )
        );
        setSuccess("Service status updated successfully.");
        return { success: true };
      }

      setError(response.message || "Failed to update service status.");
      return { success: false };
    } catch (err) {
      console.error("Error updating service status:", err);
      setError("An error occurred while updating the service status.");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage > 0 && newPage <= totalPages) {
        setCurrentPage(newPage);
      }
    },
    [totalPages]
  );

  const resetAlerts = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);

  useEffect(() => {
    loadServices({ page: currentPage, search: searchValue });
  }, [currentPage, searchValue, loadServices]);

  return {
    services,
    serviceTypes,
    searchValue,
    setSearchValue,
    currentPage,
    totalPages,
    totalItems,
    loading,
    error,
    success,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleStatusToggle,
    handlePageChange,
    resetAlerts,
    setCurrentPage,
  };
};

export default useServices;
